import type { CreateSlideConfig } from "./types";

/**
 * Office Dialog window size, as a PERCENTAGE of the screen (the Office Dialog
 * API only accepts percentages and can't resize per step). Deliberately large
 * so the window acts as the modal SCRIM: the CSS floats a compact centred card
 * with a dark backdrop around it, and Office centres the window on screen.
 */
const DIALOG_SIZE = { height: 80, width: 82 } as const;

/** Relative path of the dialog page served from the add-in origin. */
const DIALOG_PAGE = "/create-slides-dialog.html";

/** Whether the Office Dialog API is available (i.e. running inside a host). */
export function canUseDialog(): boolean {
  return (
    typeof Office !== "undefined" &&
    typeof Office.context?.ui?.displayDialogAsync === "function"
  );
}

/**
 * Opens the "Create slides" wizard in an Office Dialog (a modal window floating
 * over PowerPoint). The dialog ONLY collects the wizard input — the finished
 * config is posted back here and generation runs in the TASK PANE, so the user
 * watches per-slide progress and sees each slide inserted live.
 *
 * File objects can't cross the messageParent boundary, so `documents[].file` is
 * dropped from the returned config (only its name/chars survive). That's fine:
 * generation sends document input via blob storage, not the raw File.
 *
 * Resolves with the collected config, or null if the user cancels/closes.
 * Rejects (with a user-facing message) if the dialog window cannot be opened —
 * so the caller can distinguish "user cancelled" (null) from "failed to open".
 */
export function openCreateSlidesDialog(): Promise<CreateSlideConfig | null> {
  return new Promise((resolve, reject) => {
    if (!canUseDialog()) {
      resolve(null);
      return;
    }
    const url = `${globalThis.location.origin}${DIALOG_PAGE}`;
    Office.context.ui.displayDialogAsync(url, { ...DIALOG_SIZE }, (asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        // Log the technical detail (Office error code/message) for diagnosis,
        // and reject with a message safe to show the user.
        const err = asyncResult.error;
        console.error(
          `[create-slide] Office failed to open the Create Slides dialog (code ${err?.code ?? "?"}): ${err?.message ?? "unknown error"}`,
        );
        reject(new Error("Couldn't open the Create Slides window. Please try again."));
        return;
      }
      const dialog = asyncResult.value;
      let settled = false;
      const settle = (value: CreateSlideConfig | null) => {
        if (settled) return;
        settled = true;
        try {
          dialog.close();
        } catch {
          /* already closed */
        }
        resolve(value);
      };
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: unknown) => {
        try {
          const { message } = arg as { message: string };
          const parsed = JSON.parse(message) as { type?: string; config?: CreateSlideConfig };
          settle(parsed.type === "generate" && parsed.config ? parsed.config : null);
        } catch (e) {
          // Malformed message from the dialog (bad JSON / unexpected shape) —
          // log before settling null so the dropped payload is traceable.
          console.error("[create-slide] failed to parse dialog message; ignoring:", e);
          settle(null);
        }
      });
      // User closed the dialog (X / host close) — resolve so we don't hang.
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => settle(null));
    });
  });
}
